import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Check,
  X,
  Coins,
  Gem,
  Gift,
} from "lucide-react";
import { DailyRewardDialog } from "@/components/ui/daily-reward-dialog";
import { apiRequest } from "@/lib/api";

// Schema for daily reward form data
const dailyRewardFormSchema = z.object({
  day: z.number().int().positive("Day must be a positive number"),
  amount: z.number().int().positive("Amount must be a positive number"),
  currencyType: z.enum(["regular", "premium"]),
  description: z
    .string()
    .min(1, "Description is required")
    .max(100, "Description too long"),
  imageUrl: z.string().optional(),
  isActive: z.boolean(),
  bonusItemId: z.number().int().positive().nullable().optional(),
});

type DailyRewardFormData = z.infer<typeof dailyRewardFormSchema>;

// Define a type for the reward data
type DailyReward = {
  id: number;
  day: number;
  amount: number;
  currencyType: "regular" | "premium";
  description: string;
  imageUrl?: string | null;
  isActive: boolean;
  bonusItemId?: number | null;
  bonusItem?: { id: number; name: string } | null; // Assuming bonusItem might be populated
};

// Define a type for shop items used in the bonus item dropdown
type ShopItemOption = {
  id: number;
  name: string;
};

export function DailyRewardManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState<DailyReward | null>(null);

  // Query to get all daily rewards
  const {
    data: rewards,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["/api/currency/daily-rewards"],
    queryFn: () => apiRequest<DailyReward[]>("/api/currency/daily-rewards"),
  });

  // Query to get all possible bonus items for the select dropdown
  const { data: shopItems } = useQuery({
    queryKey: ["/api/currency/shop"],
    queryFn: () => apiRequest<ShopItemOption[]>("/api/currency/shop"), // Fetch only necessary fields
  });

  // Mutation to add a new daily reward
  const addRewardMutation = useMutation({
    mutationFn: async (data: DailyRewardFormData) => {
      return apiRequest<DailyReward>("/api/currency/daily-rewards", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/daily-rewards"] });
      toast({
        title: "Reward added successfully",
        description: "The new daily reward has been added.",
      });
      setIsAddDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding reward",
        description:
          error.message ?? "Failed to add the reward. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to update a daily reward
  const updateRewardMutation = useMutation({
    mutationFn: async (data: { id: number; reward: DailyRewardFormData }) => {
      return apiRequest<DailyReward>(`/api/currency/daily-rewards/${data.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.reward),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/daily-rewards"] });
      toast({
        title: "Reward updated successfully",
        description: "The daily reward has been updated.",
      });
      setIsEditDialogOpen(false);
      setSelectedReward(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating reward",
        description:
          error.message ?? "Failed to update the reward. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete a daily reward
  const deleteRewardMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest<void>(`/api/currency/daily-rewards/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/daily-rewards"] });
      toast({
        title: "Reward deleted successfully",
        description: "The daily reward has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting reward",
        description:
          error.message ?? "Failed to delete the reward. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (data: DailyRewardFormData) => {
    try {
      const validatedData = dailyRewardFormSchema.parse(data);
      addRewardMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (data: DailyRewardFormData) => {
    if (!selectedReward) return;
    try {
      const validatedData = dailyRewardFormSchema.parse(data);
      updateRewardMutation.mutate({ id: selectedReward.id, reward: validatedData });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    deleteRewardMutation.mutate(id);
  };

  const openEditDialog = (reward: DailyReward) => {
    setSelectedReward(reward);
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading daily rewards</h3>
        <p className="text-muted-foreground">{error.message}</p>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add New Reward
        </Button>
      </div>

      <Table>
        <TableCaption>A list of all daily rewards.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Day</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Reward</TableHead>
            <TableHead>Bonus Item</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!rewards || rewards.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                No daily rewards found. Add rewards to get started.
              </TableCell>
            </TableRow>
          ) : (
            rewards.map((reward) => (
              <TableRow key={reward.id}>
                <TableCell className="font-medium">{reward.day}</TableCell>
                <TableCell>{reward.description}</TableCell>
                <TableCell>
                  <span className="flex items-center">
                    {reward.currencyType === "premium" ? (
                      <Gem className="h-4 w-4 mr-1 text-purple-500" />
                    ) : (
                      <Coins className="h-4 w-4 mr-1 text-amber-500" />
                    )}
                    {reward.amount}
                  </span>
                </TableCell>
                <TableCell>
                  {reward.bonusItemId ? (
                    <span className="flex items-center text-sm text-muted-foreground">
                      <Gift className="h-4 w-4 mr-1 text-blue-500" />
                      {shopItems?.find((item) => item.id === reward.bonusItemId)?.name ?? `Item #${reward.bonusItemId}`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell>
                  {reward.isActive ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openEditDialog(reward)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Daily Reward</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the reward for Day {reward.day}? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(reward.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <DailyRewardDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddSubmit}
        mode="add"
        isLoading={addRewardMutation.isPending}
        shopItems={shopItems ?? []}
      />

      {selectedReward && (
        <DailyRewardDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit}
          initialData={selectedReward}
          mode="edit"
          isLoading={updateRewardMutation.isPending}
          shopItems={shopItems ?? []}
        />
      )}
    </div>
  );
}
